// src/pages/Dashboard.js
import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Grid, Paper, TextField, Button,
    CircularProgress, Alert, Card, CardContent, Table,
    TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import api from '../api/index.js';
import { format } from 'date-fns';

// Helper para formatear moneda
const formatCurrency = (value) => {
    const numberValue = Number(value);
    if (isNaN(numberValue)) return '$0.00';
    return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN'
    }).format(numberValue);
};

// Componente reutilizable para las tarjetas de resumen
const SummaryCard = ({ title, value, color }) => (
    <Card sx={{ height: '100%' }}>
        <CardContent>
            <Typography color="text.secondary" gutterBottom>
                {title}
            </Typography>
            <Typography variant="h4" component="div" sx={{ color: color || 'text.primary' }}>
                {value}
            </Typography>
        </CardContent>
    </Card>
);

const Dashboard = () => {
    // --- ESTADOS ---
    const [data, setData] = useState(null);
    const [filters, setFilters] = useState({
        startDate: format(new Date(), 'yyyy-MM-dd'),
        endDate: format(new Date(), 'yyyy-MM-dd'),
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // --- FUNCIONES ---
    const fetchData = async () => {
        setLoading(true);
        setError('');
        try {
            // Nota: El endpoint '/reports/dashboard-analytics' debe ser creado en el backend.
            const params = new URLSearchParams(filters).toString();
            const response = await api.get(`/reports/dashboard-analytics?${params}`);
            setData(response.data);
        } catch (err) {
            setError('No se pudieron cargar las analíticas.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value });
    };

    useEffect(() => {
        fetchData();
        
    }, []); 
    // --- RENDERIZADO ---
    return (
        <Box>
            <Typography variant="h4" gutterBottom>
                Dashboard de Analíticas
            </Typography>

            {/* --- FILTROS --- */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Fecha de Inicio"
                            name="startDate"
                            type="date"
                            value={filters.startDate}
                            onChange={handleFilterChange}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Fecha de Fin"
                            name="endDate"
                            type="date"
                            value={filters.endDate}
                            onChange={handleFilterChange}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Button
                            variant="contained"
                            fullWidth
                            size="large"
                            onClick={fetchData}
                            disabled={loading}
                        >
                            {loading ? 'Cargando...' : 'Aplicar Filtros'}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {error && <Alert severity="error">{error}</Alert>}
            {loading && <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>}

            {/* --- SECCIÓN DE DATOS (SOLO SE MUESTRA SI HAY DATOS Y NO HAY ERROR) --- */}
            {data && !loading && !error && (
                <>
                    {/* --- RESÚMENES TOTALES --- */}
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6} md={3}><SummaryCard title="Ventas Totales" value={formatCurrency(data.totals.total_sales)} color="success.main" /></Grid>
                        <Grid item xs={12} sm={6} md={3}><SummaryCard title="Ganancia Bruta" value={formatCurrency(data.totals.gross_profit)} color="primary.main" /></Grid>
                        <Grid item xs={12} sm={6} md={3}><SummaryCard title="Gastos" value={formatCurrency(data.totals.total_expenses)} color="error.main" /></Grid>
                        <Grid item xs={12} sm={6} md={3}><SummaryCard title="Utilidad Neta" value={formatCurrency(data.totals.net_profit)} color="info.main" /></Grid>
                        <Grid item xs={12} sm={6} md={3}><SummaryCard title="Total Compras" value={formatCurrency(data.totals.total_purchases)} color="warning.main" /></Grid>
                        <Grid item xs={12} sm={6} md={3}><SummaryCard title="Deuda a Proveedores" value={formatCurrency(data.totals.pending_supplier_debt)} color="orange" /></Grid>
                    </Grid>

                    {/* --- DESGLOSES --- */}
                    <Grid container spacing={3}>
                        {/* Desglose por Tienda */}
                        <Grid item xs={12} md={7}>
                            <Paper sx={{ p: 2, height: '100%' }}>
                                <Typography variant="h6" gutterBottom>Resumen por Tienda</Typography>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Tienda</TableCell>
                                                <TableCell align="right">Ventas</TableCell>
                                                <TableCell align="right">G. Bruta</TableCell>
                                                <TableCell align="right">Gastos</TableCell>
                                                <TableCell align="right">U. Neta</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {data.summaryByStore.map((row) => (
                                                <TableRow key={row.store_id}>
                                                    <TableCell>{row.store_name}</TableCell>
                                                    <TableCell align="right">{formatCurrency(row.total_sales)}</TableCell>
                                                    <TableCell align="right">{formatCurrency(row.gross_profit)}</TableCell>
                                                    <TableCell align="right">{formatCurrency(row.total_expenses)}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(row.net_profit)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Grid>

                        {/* Desglose por Categoría */}
                        <Grid item xs={12} md={5}>
                            <Paper sx={{ p: 2, height: '100%' }}>
                                <Typography variant="h6" gutterBottom>Ventas por Categoría</Typography>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Categoría</TableCell>
                                                <TableCell align="right">Ventas</TableCell>
                                                <TableCell align="right">Ganancia</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {data.salesByCategory.map((row) => (
                                                <TableRow key={row.category_name}>
                                                    <TableCell>{row.category_name || 'Sin Categoría'}</TableCell>
                                                    <TableCell align="right">{formatCurrency(row.total_sales)}</TableCell>
                                                    <TableCell align="right">{formatCurrency(row.total_profit)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Grid>
                    </Grid>
                </>
            )}
        </Box>
    );
};

export default Dashboard;